import { useCallback, useRef, useState, type FormEvent } from "react";
import { CheckCircle, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { config } from "../config";
import { Turnstile } from "./Turnstile";

/** Same endpoint + shape as the in-app FeedbackWidget. */
const WORKER_URL = "https://design-chef-bot.wr7thful.workers.dev/feedback";

interface ContactFormProps {
  /** Rendered above the form ("send us a message" context). */
  title?: string;
}

export function ContactForm({ title = "send us a message" }: ContactFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const turnstileEnabled = config.turnstileSiteKey !== "";

  const reset = useCallback(() => {
    setName("");
    setEmail("");
    setMessage("");
    setSent(false);
    setError(false);
    setShaking(false);
    setTurnstileToken(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!message.trim()) {
        setShaking(true);
        window.setTimeout(() => setShaking(false), 500);
        return;
      }
      if (turnstileEnabled && !turnstileToken) {
        setShaking(true);
        window.setTimeout(() => setShaking(false), 500);
        return;
      }

      setError(false);
      setSending(true);

      try {
        // Honeypot filled = bot. Pretend success, send nothing.
        if (honeypot.trim()) {
          setSent(true);
          setSending(false);
          setName("");
          setEmail("");
          setMessage("");
          return;
        }

        const res = await fetch(WORKER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project: "yay",
            kind: "contact",
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            message: message.trim(),
            page: location.host + location.pathname,
            ...(turnstileEnabled ? { turnstileToken } : {}),
          }),
        });

        const data = (await res.json()) as { ok?: boolean };

        if (res.ok && data.ok) {
          setSent(true);
          setName("");
          setEmail("");
          setMessage("");
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setSending(false);
      }
    },
    [message, name, email, honeypot, turnstileEnabled, turnstileToken],
  );

  return (
    <div className="contact-form-wrap">
      {!sent && <h2 className="contact-form-title">{title}</h2>}
      {sent ? (
        <div className="contact-success">
          <CheckCircle size={40} weight="fill" />
          <span>thanks — we got it.</span>
          <button
            type="button"
            className="contact-success-again"
            onClick={reset}
          >
            send another
          </button>
        </div>
      ) : (
        <form
          ref={formRef}
          className={`contact-form${shaking ? " contact-shake" : ""}`}
          onSubmit={handleSubmit}
        >
          <label className="contact-field">
            <span>name (optional)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoComplete="name"
              disabled={sending}
            />
          </label>
          <label className="contact-field">
            <span>email (optional — if you'd like a reply)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={100}
              autoComplete="email"
              disabled={sending}
            />
          </label>
          <label className="contact-field">
            <span>message</span>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              placeholder={
                shaking ? "say something first" : "how can we help?"
              }
              disabled={sending}
            />
          </label>
          {/* Honeypot — hidden from humans, irresistible to bots. */}
          <input
            type="text"
            className="contact-hp"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          {turnstileEnabled && (
            <Turnstile siteKey={config.turnstileSiteKey} onToken={setTurnstileToken} />
          )}
          <button
            type="submit"
            className="contact-submit"
            disabled={sending || (turnstileEnabled && !turnstileToken)}
          >
            {sending ? (
              <>
                <Spinner
                  size={16}
                  weight="bold"
                  className="feedback-spinner"
                />
                <span>sending...</span>
              </>
            ) : (
              <>
                <PaperPlaneTilt size={16} weight="fill" />
                <span>{error ? "couldn't send. try again?" : "send"}</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
